/**
 * CrossDockManifestsPage — `/mail/manifests`.
 *
 * Live operator surface backed by service-mail `/v1/manifests/*`. Renders
 * a list with filters + a "New manifest" modal + an inline detail view
 * with item-add/remove + state-machine action buttons + CSV download.
 */
import { type JSX, useEffect, useState } from 'react';
import { Truck, Plus, Package, Loader2, Download } from 'lucide-react';

import { PageHeader, StatusBadge } from '../shared/StaffUI';
import {
  addManifestItems,
  createManifest,
  dispatchManifest,
  getManifest,
  listManifests,
  manifestCsvUrl,
  receiveManifest,
  removeManifestItem,
  sealManifest,
  type ManifestDetailDto,
  type ManifestDto,
  type ManifestStatus,
} from '../../lib/api/manifests';

const STATUSES: readonly ManifestStatus[] = ['DRAFT', 'SEALED', 'IN_TRANSIT', 'RECEIVED', 'DISCREPANCY'];

export function CrossDockManifestsPage(): JSX.Element {
  const [items, setItems] = useState<readonly ManifestDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<ManifestStatus | ''>('');
  const [filterOrigin, setFilterOrigin] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState<ManifestDetailDto | null>(null);

  async function refresh(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const res = await listManifests({
        ...(filterStatus && { status: filterStatus }),
        ...(filterOrigin && { origin: filterOrigin }),
        pageSize: 50,
      });
      setItems(res.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load manifests');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // note: deps intentionally narrow — rule not enabled in this config
  }, [filterStatus, filterOrigin]);

  async function openDetail(id: string): Promise<void> {
    try {
      setSelected(await getManifest(id));
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Load failed');
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Cross-Dock Manifests"
        description="Outbound bag / cage manifests linking sort bins to a vehicle dispatch."
        actions={
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark"
            data-testid="manifest-new-btn"
          >
            <Plus className="w-4 h-4" /> New manifest
          </button>
        }
      />

      <div className="flex flex-wrap items-end gap-3 bg-white border border-border rounded-xl p-4">
        <div>
          <label className="block text-[10px] uppercase text-muted mb-1" htmlFor="m-status">Status</label>
          <select
            id="m-status"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as ManifestStatus | '')}
            className="border border-border rounded px-3 py-1.5 text-sm"
            data-testid="manifest-filter-status"
          >
            <option value="">Any</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase text-muted mb-1" htmlFor="m-origin">Origin outlet</label>
          <input
            id="m-origin"
            type="text"
            value={filterOrigin}
            onChange={(e) => setFilterOrigin(e.target.value)}
            placeholder="NG-LAG-VI-001"
            className="border border-border rounded px-3 py-1.5 text-sm font-mono"
          />
        </div>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-muted ml-2" />}
      </div>

      {error && <div className="bg-danger-light border border-danger text-danger text-sm rounded-xl p-3">{error}</div>}

      <div className="bg-white border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm" data-testid="manifest-table">
          <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
            <tr>
              <th className="text-left px-4 py-2">Manifest</th>
              <th className="text-left px-4 py-2"><Truck className="inline w-3 h-3" /> Route</th>
              <th className="text-left px-4 py-2">Vehicle</th>
              <th className="text-left px-4 py-2"><Package className="inline w-3 h-3" /> Items</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">Created</th>
              <th className="text-right px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.length === 0 && !loading && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-muted text-xs">No manifests match the filters.</td></tr>
            )}
            {items.map((m) => (
              <tr key={m.id} className="hover:bg-gray-50" data-testid={`manifest-row-${m.manifestNumber}`}>
                <td className="px-4 py-2 font-mono text-xs font-bold">{m.manifestNumber}</td>
                <td className="px-4 py-2 text-xs">{m.originOutletCode} → {m.destinationOutletCode}</td>
                <td className="px-4 py-2 text-xs text-muted">{m.vehicleRef ?? '—'}</td>
                <td className="px-4 py-2 text-xs">{m.itemCount}</td>
                <td className="px-4 py-2"><StatusBadge status={m.status} /></td>
                <td className="px-4 py-2 text-xs text-muted">{new Date(m.createdAt).toLocaleString()}</td>
                <td className="px-4 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => void openDetail(m.id)}
                    className="text-xs text-nipost-green underline"
                    data-testid={`manifest-open-${m.manifestNumber}`}
                  >
                    Open
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <CreateManifestModal
          onClose={() => setShowCreate(false)}
          onCreated={(m) => { setShowCreate(false); void refresh(); void openDetail(m.id); }}
        />
      )}
      {selected && (
        <ManifestDetailModal
          manifest={selected}
          onClose={() => setSelected(null)}
          onChange={async () => {
            const fresh = await getManifest(selected.id);
            setSelected(fresh);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

interface CreateManifestModalProps {
  onClose: () => void;
  onCreated: (m: ManifestDto) => void;
}

function CreateManifestModal({ onClose, onCreated }: CreateManifestModalProps): JSX.Element {
  const [origin, setOrigin] = useState('NG-LAG-VI-001');
  const [destination, setDestination] = useState('NG-FCT-WUS-001');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const m = await createManifest({ originOutletCode: origin, destinationOutletCode: destination });
      onCreated(m);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="manifest-create-modal">
      <div className="bg-white rounded-xl p-6 w-full max-w-md space-y-4">
        <h3 className="font-semibold text-lg">New manifest</h3>
        {error && <div className="text-sm text-danger">{error}</div>}
        <div>
          <label className="block text-xs text-muted mb-1" htmlFor="origin-outlet">Origin outlet</label>
          <input id="origin-outlet" type="text" value={origin} onChange={(e) => setOrigin(e.target.value)} className="w-full border border-border rounded px-3 py-2 font-mono" data-testid="manifest-create-origin" />
        </div>
        <div>
          <label className="block text-xs text-muted mb-1" htmlFor="dest-outlet">Destination outlet</label>
          <input id="dest-outlet" type="text" value={destination} onChange={(e) => setDestination(e.target.value)} className="w-full border border-border rounded px-3 py-2 font-mono" data-testid="manifest-create-destination" />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm border border-border rounded">Cancel</button>
          <button type="button" onClick={() => void submit()} disabled={busy || !origin || !destination} className="px-3 py-2 text-sm bg-nipost-green text-white rounded disabled:opacity-50" data-testid="manifest-create-submit">
            {busy ? 'Creating…' : 'Create draft'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface ManifestDetailModalProps {
  manifest: ManifestDetailDto;
  onClose: () => void;
  onChange: () => Promise<void>;
}

function ManifestDetailModal({ manifest, onClose, onChange }: ManifestDetailModalProps): JSX.Element {
  const [trackingInput, setTrackingInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function addItems(): Promise<void> {
    const tns = trackingInput.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
    if (tns.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await addManifestItems(manifest.id, tns);
      setTrackingInput('');
      await onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Add failed');
    } finally {
      setBusy(false);
    }
  }

  async function action(label: string, fn: () => Promise<unknown>): Promise<void> {
    if (!window.confirm(`Confirm: ${label}?`)) return;
    setBusy(true);
    setError(null);
    try {
      await fn();
      await onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : `${label} failed`);
    } finally {
      setBusy(false);
    }
  }

  async function removeItem(tn: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await removeManifestItem(manifest.id, tn);
      await onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Remove failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-6" data-testid="manifest-detail-modal">
      <div className="bg-white rounded-xl p-6 w-full max-w-3xl space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-lg font-mono" data-testid="manifest-detail-number">{manifest.manifestNumber}</h3>
            <p className="text-xs text-muted">{manifest.originOutletCode} → {manifest.destinationOutletCode}</p>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={manifest.status} />
            <a
              href={manifestCsvUrl(manifest.id)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-nipost-green underline"
              data-testid="manifest-csv-link"
            >
              <Download className="w-3 h-3" /> CSV
            </a>
          </div>
        </div>

        {error && <div className="bg-danger-light border border-danger text-danger text-sm rounded-lg p-2">{error}</div>}

        {manifest.status === 'DRAFT' && (
          <div className="bg-gray-50 border border-border rounded-lg p-3 space-y-2">
            <label className="block text-xs text-muted" htmlFor="tns-input">Add tracking numbers (comma or space separated)</label>
            <textarea
              id="tns-input"
              value={trackingInput}
              onChange={(e) => setTrackingInput(e.target.value)}
              rows={2}
              className="w-full border border-border rounded px-3 py-2 text-sm font-mono"
              data-testid="manifest-add-tracking-input"
            />
            <div className="flex justify-end">
              <button type="button" onClick={() => void addItems()} disabled={busy || !trackingInput.trim()} className="px-3 py-1.5 text-sm bg-nipost-green text-white rounded disabled:opacity-50" data-testid="manifest-add-items-btn">
                Add items
              </button>
            </div>
          </div>
        )}

        <div>
          <h4 className="font-semibold text-sm mb-2">Items ({manifest.items.length})</h4>
          {manifest.items.length === 0 ? (
            <p className="text-xs text-muted">No items yet.</p>
          ) : (
            <table className="w-full text-sm" data-testid="manifest-items-table">
              <thead className="text-[10px] uppercase text-muted">
                <tr>
                  <th className="text-left py-1">Tracking</th>
                  <th className="text-left py-1">Added</th>
                  <th className="text-right py-1"></th>
                </tr>
              </thead>
              <tbody>
                {manifest.items.map((it) => (
                  <tr key={it.id} className="border-t border-border">
                    <td className="py-1 font-mono text-xs">{it.trackingNumber}</td>
                    <td className="py-1 text-xs text-muted">{new Date(it.addedAt).toLocaleString()}</td>
                    <td className="py-1 text-right">
                      {manifest.status === 'DRAFT' && (
                        <button type="button" onClick={() => void removeItem(it.trackingNumber)} className="text-xs text-red-600 underline">
                          remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex justify-between items-center pt-2 border-t border-border">
          <div className="flex gap-2">
            {manifest.status === 'DRAFT' && (
              <button
                type="button"
                onClick={() => void action('Seal', () => sealManifest(manifest.id))}
                disabled={busy || manifest.items.length === 0}
                className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded disabled:opacity-50"
                data-testid="manifest-seal-btn"
              >
                Seal
              </button>
            )}
            {manifest.status === 'SEALED' && (
              <button
                type="button"
                onClick={() => void action('Dispatch', () => dispatchManifest(manifest.id))}
                disabled={busy}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded disabled:opacity-50"
                data-testid="manifest-dispatch-btn"
              >
                Dispatch
              </button>
            )}
            {manifest.status === 'IN_TRANSIT' && (
              <button
                type="button"
                onClick={() => void action('Receive', () => receiveManifest(manifest.id))}
                disabled={busy}
                className="px-3 py-1.5 text-sm bg-success text-white rounded disabled:opacity-50"
                data-testid="manifest-receive-btn"
              >
                Receive
              </button>
            )}
          </div>
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm border border-border rounded">Close</button>
        </div>
      </div>
    </div>
  );
}
